import { Box } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';

import { getRouteReportsSavingsSummary } from 'shared/const/router';
import { formatReportCurrency } from 'shared/lib/reportFormatters';
import { ReportDrillInHeader } from 'shared/ui/ReportDrillInHeader';
import { ReportMetric } from 'shared/ui/ReportMetric/ReportMetric';
import {
    ComposableReportWidget,
    type ReportWidgetConfig,
    type ReportWidgetItem,
} from 'widgets/ComposableReportWidget';
import { Page } from 'widgets/Page';

import { useReportsPageSavingsSummaryProduct } from '../model/hooks/useReportsPageSavingsSummaryProduct';

const ReportsPageSavingsSummaryProduct = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const {
        sku,
        product: productFromState,
        fromMissedSavings,
        loading,
        dateFrom,
        dateTo,
        datePreset,
        totalSaved,
        totalSpent,
        baselineTotal,
        totalQuantity,
        ordersCount,
        ordersData,
        savingsPercentLabel,
        avgSupplierPricePerUnit,
        lastRequestParamsPresent,
        canLoadMoreOrders,
        loadMoreOrders,
    } = useReportsPageSavingsSummaryProduct();

    const handleBack = useCallback(() => {
        navigate(getRouteReportsSavingsSummary(), {
            state: { fromDrillIn: true },
        });
    }, [navigate]);

    if (!sku || !lastRequestParamsPresent) {
        return <Navigate to={getRouteReportsSavingsSummary()} replace />;
    }

    return (
        <Page
            dataTestId="reports-page-savings-summary-product"
            padding="24px 35px"
        >
            <Box>
                <ReportDrillInHeader
                    title={productFromState?.itemName ?? sku}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    datePreset={datePreset}
                    onBack={handleBack}
                    backButtonTitle={t('Savings Summary')}
                    backButtonId="back-to-savings-summary-button"
                />

                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        marginBottom: 3,
                    }}
                >
                    <ReportMetric
                        title={t(
                            'savingsSummary.productsMetricTotalSavings',
                            'Total Savings',
                        )}
                        primary={formatReportCurrency(totalSaved)}
                        primaryColor="metrics.positive"
                        secondary={
                            savingsPercentLabel
                                ? `(${savingsPercentLabel} ${t(
                                      'savingsSummary.savedSuffix',
                                  )})`
                                : undefined
                        }
                        secondarySx={{
                            color: 'metrics.positive',
                            fontSize: '14px',
                            lineHeight: '20px',
                            fontWeight: 500,
                        }}
                        loading={loading}
                        minHeight="172px"
                    >
                        <Box
                            sx={{
                                fontSize: '12px',
                                lineHeight: '16px',
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            <span>
                                {t(
                                    'savingsSummary.productsMetricCompetitiveSpend',
                                    'Competitive Spend',
                                )}
                            </span>
                            <Box
                                component="span"
                                sx={{
                                    fontVariantNumeric: 'tabular-nums',
                                    fontWeight: 600,
                                }}
                            >
                                {formatReportCurrency(baselineTotal)}
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                fontSize: '12px',
                                lineHeight: '16px',
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            <span>
                                {t(
                                    'savingsSummary.productsMetricAvgSupplierPrice',
                                    'Avg Supplier Price',
                                )}
                            </span>
                            <Box
                                component="span"
                                sx={{
                                    fontVariantNumeric: 'tabular-nums',
                                    fontWeight: 600,
                                }}
                            >
                                {avgSupplierPricePerUnit == null
                                    ? '-'
                                    : formatReportCurrency(
                                          avgSupplierPricePerUnit,
                                      )}
                            </Box>
                        </Box>
                    </ReportMetric>
                    <ReportMetric
                        title={t(
                            'savingsSummary.productsMetricTotalSpent',
                            'Total Spent',
                        )}
                        primary={formatReportCurrency(totalSpent)}
                        loading={loading}
                        minHeight="172px"
                    />
                    <ReportMetric
                        title={t(
                            'savingsSummary.productsMetricTotalQuantity',
                            'Total Quantity',
                        )}
                        primary={totalQuantity.toLocaleString()}
                        loading={loading}
                        minHeight="172px"
                    />
                    <ReportMetric
                        title={t(
                            'savingsSummary.productsMetricOrders',
                            'Orders',
                        )}
                        primary={ordersCount.toLocaleString()}
                        loading={loading}
                        minHeight="172px"
                    />
                </Box>

                <Box sx={{ marginTop: 4 }}>
                    <ComposableReportWidget
                        config={
                            {
                                widgetId: 'savings-summary-product-orders',
                                title: t(
                                    'savingsSummary.productsPurchasesTitle',
                                    'Purchases',
                                ),
                                description: t(
                                    'savingsSummary.productsPurchasesSubtitle',
                                    'Each purchase per order per property',
                                ),
                                layout: 'stacked',
                                visualization: {
                                    enabled: false,
                                    type: 'bar',
                                },
                                pagination: {
                                    mode: 'server',
                                    pageSize: 25,
                                    canLoadMore: canLoadMoreOrders,
                                    onLoadMore: loadMoreOrders,
                                },
                                table: {
                                    enabled: true,
                                    columns: [
                                        {
                                            id: 'date',
                                            label: t('Date'),
                                            valueKey: 'date',
                                            format: 'text',
                                            align: 'left',
                                        },
                                        {
                                            id: 'order',
                                            label: t('Order'),
                                            valueKey: 'orderNumber',
                                            format: 'text',
                                            align: 'left',
                                            bold: true,
                                        },
                                        {
                                            id: 'property',
                                            label: t('Property'),
                                            valueKey: 'propertyName',
                                            format: 'text',
                                            align: 'left',
                                        },
                                        {
                                            id: 'supplier',
                                            label: t('Supplier'),
                                            valueKey: 'suppliers',
                                            format: 'text',
                                            align: 'left',
                                        },
                                        {
                                            id: 'quantity',
                                            label: t('Quantity'),
                                            valueKey: 'quantity',
                                            format: 'number',
                                            align: 'right',
                                        },
                                        {
                                            id: 'pricePaid',
                                            label: t('Price Paid'),
                                            valueKey: 'pricePaid',
                                            format: 'currency',
                                            align: 'right',
                                        },
                                        {
                                            id: 'avgSupplierPrice',
                                            label: fromMissedSavings
                                                ? t(
                                                      'savingsSummary.missedSavingsColumnBestPrice',
                                                      'Best Price',
                                                  )
                                                : t('Avg Supplier Price'),
                                            valueKey: 'avgSupplierPrice',
                                            format: 'currency',
                                            align: 'right',
                                        },
                                        {
                                            id: 'savings',
                                            label: fromMissedSavings
                                                ? t(
                                                      'savingsSummary.missedSavings',
                                                      'Missed Savings',
                                                  )
                                                : t('Savings'),
                                            valueKey: 'savings',
                                            format: 'currency',
                                            align: 'right',
                                            highlightColor: fromMissedSavings
                                                ? 'metrics.warning'
                                                : 'metrics.positive',
                                        },
                                    ],
                                    options: {
                                        emptyMessage: t(
                                            'savingsSummary.productsPurchasesEmpty',
                                            'No purchases found for this product',
                                        ),
                                    },
                                },
                            } as ReportWidgetConfig
                        }
                        items={(ordersData?.items ?? []).map(
                            (orderItem, index): ReportWidgetItem => {
                                const submittedDate = new Date(
                                    orderItem.submittedAt,
                                );
                                const dateLabel = new Intl.DateTimeFormat(
                                    undefined,
                                    {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                    },
                                ).format(submittedDate);

                                let avgSupplierTotal = 0;
                                let savingsValue = 0;

                                if (fromMissedSavings) {
                                    savingsValue = orderItem.totalMissed ?? 0;
                                    avgSupplierTotal =
                                        orderItem.totalPaid - savingsValue;
                                } else {
                                    savingsValue = orderItem.totalSaved;
                                    avgSupplierTotal =
                                        orderItem.baselinePrice *
                                        orderItem.quantity;
                                }

                                return {
                                    id: `${orderItem.order.orderId}-${index}`,
                                    label: orderItem.order.orderNumber,
                                    primaryValue: savingsValue,
                                    secondaryValues: {
                                        date: dateLabel,
                                        orderNumber:
                                            orderItem.order.orderNumber,
                                        propertyName: orderItem.propertyName,
                                        suppliers:
                                            orderItem.suppliers.join(', '),
                                        quantity: orderItem.quantity,
                                        pricePaid: orderItem.totalPaid,
                                        avgSupplierPrice: avgSupplierTotal,
                                        savings: savingsValue,
                                    },
                                    meta: { raw: orderItem },
                                };
                            },
                        )}
                        loading={loading}
                    />
                </Box>
            </Box>
        </Page>
    );
};

export default ReportsPageSavingsSummaryProduct;
